<template>
  <v-card class="workers-list-card" elevation="1" style="border-radius: 12px;">
    <v-card-title class="pb-0 d-block">
      <div class="d-flex align-center">
        <span class="text-h6">Workers</span>
        <v-spacer />
      </div>
      <div class="subtitle text-caption text--secondary mt-1">{{ workers.length }} workers actifs</div>
      <v-divider class="mt-2" />
    </v-card-title>

    <v-card-text class="pt-2">
      <div class="workers-container" role="list">
        <div
          v-for="w in sortedWorkers"
          :key="w.id"
          class="worker-card"
          :class="{ pulse: pulseRow[w.id] }"
          role="listitem"
        >
          <!-- Line 1: status badge + ID (left) / time (right) -->
          <div class="line line-top">
            <div class="left">
              <v-chip x-small :color="getStatusBadgeColor(w.status)" text-color="white" class="mr-2">{{ getStatusText(w.status) }}</v-chip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span class="worker-id" v-bind="attrs" v-on="on">{{ abbreviatedId(w.id) }}</span>
                </template>
                <span>{{ w.id }}</span>
              </v-tooltip>
            </div>
            <div class="right time text--secondary">
              <v-icon x-small class="mr-1" color="grey">mdi-clock-outline</v-icon>
              {{ formatAge(w.createdAt) }}
            </div>
          </div>

          <!-- Line 2: host -->
          <div class="line text--secondary">
            <v-icon x-small class="mr-1" color="grey">mdi-desktop-classic</v-icon>
            <span class="ellipsis">{{ w.host }}</span>
          </div>

          <!-- Line 3: queue -->
          <div class="line text--secondary">
            <span class="muted">File:</span>
            <span class="ml-1 queue-link ellipsis" @click="$emit('filter-by-queue', w.queue)">{{ w.queue }}</span>
          </div>

          <!-- Line 4 (optional): current job if busy) -->
          <div class="line" v-if="w.status === 'busy' && w.currentJob">
            <a href="#" class="job-link ellipsis" @click.prevent="$emit('job-click', w)">{{ w.currentJob }}</a>
          </div>
          <div class="line text--secondary" v-if="w.status === 'busy' && w.jobId">
            <span class="muted">jobId:</span>
            <span class="ml-1 ellipsis">{{ w.jobId }}</span>
          </div>
          <div class="line text--secondary" v-if="w.status === 'busy' && w.executionId">
            <span class="muted">executionId:</span>
            <span class="ml-1 ellipsis">{{ w.executionId }}</span>
          </div>
        </div>

        <div v-if="workers.length === 0" class="empty-state text-center py-4">Aucun worker actif</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'WorkersListCard',
  props: {
    workers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      lastById: {},
      pulseRow: {}
    };
  },
  computed: {
    sortedWorkers() {
      // Sort by createdAt (newest first). Items without createdAt go last.
      return [...this.workers].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
    }
  },
  watch: {
    workers: {
      handler(newVal) {
        const next = {};
        for (const w of newVal) {
          const prev = this.lastById[w.id] || {};
          if ((prev.status !== 'busy' && w.status === 'busy') || (prev.status === 'busy' && w.status !== 'busy')) {
            this.$set(this.pulseRow, w.id, true);
            setTimeout(() => this.$delete(this.pulseRow, w.id), 300);
          }
          next[w.id] = { status: w.status };
        }
        this.lastById = next;
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    abbreviatedId(id) {
      if (!id) return '';
      const s = id.toString();
      if (s.length <= 8) return s;
      return s.substring(0, 8) + '…';
    },
    formatAge(createdAt) {
      if (!createdAt) return 'Âge: —';
      const now = Date.now();
      const ts = new Date(createdAt).getTime();
      if (!ts) return 'Âge: —';
      let diff = Math.max(0, Math.floor((now - ts) / 1000)); // seconds
      if (diff < 60) return `Âge: ${diff}s`;
      const mins = Math.floor(diff / 60);
      if (mins < 60) return `Âge: ${mins}m`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `Âge: ${hours}h`;
      const days = Math.floor(hours / 24);
      return `Âge: ${days}j`;
    },
    getStatusBadgeColor(status) {
      const colors = { busy: 'blue', idle: 'warning'};
      return colors[status] || 'grey';
    },
    getStatusText(status) {
      const texts = { busy: 'En cours', idle: 'En attente' };
      return texts[status] || status;
    }
  }
};
</script>

<style lang="scss">
.workers-list-card {
  height: 100%;
  display: flex;
  flex-direction: column;

  .v-card-text {
    flex: 1;
    overflow-y: auto;
    padding-top: 8px !important;
  }
}

.subtitle { line-height: 1; }

.workers-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.worker-card {
  flex: 1 0 33%;
  box-sizing: border-box;
  background: #f7f7f8; // very light grey
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px;
}

.line { display: flex; align-items: center; min-height: 20px; }
.line + .line { margin-top: 4px; }
.line-top { justify-content: space-between; }

.left { display: inline-flex; align-items: center; min-width: 0; }
.right { display: inline-flex; align-items: center; }

.worker-id { font-weight: 600; font-size: 14px; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.queue-link { color: #374151; cursor: pointer; }
.queue-link:hover { text-decoration: underline; }

.job-link { color: #2563eb; text-decoration: none; }
.job-link:hover { text-decoration: underline; }

.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.muted { color: #6b7280; }

.pulse { animation: pulseCard 0.3s ease; }
@keyframes pulseCard { 0% { background-color: rgba(59,130,246,0.08);} 100% { background-color: transparent; } }

.empty-state { color: #6b7280; }

@media (max-width: 768px) {
  .workers-list-card { height: 100%; }
}
</style>